import type { ReactNode } from 'react';

/**
 * Types for filter sensitivity
 */
export type Sensitivity = 'base' | 'accent' | 'case' | 'variant';

/**
 * Interface for filter options
 */
export interface FilterOptions {
  /**
   * Sensitivity level for string comparisons:
   * - 'base': ignore case and accents
   * - 'accent': ignore case but consider accents
   * - 'case': consider case but ignore accents
   * - 'variant': consider both case and accents
   */
  sensitivity?: Sensitivity;
}

/**
 * Create a collator based on sensitivity option
 *
 * https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator
 *
 * @param sensitivity - The sensitivity setting to use
 * @returns An Intl.Collator instance
 */
export function createCollator(sensitivity: Sensitivity = 'base'): Intl.Collator {
  return new Intl.Collator('fr', { sensitivity });
}

/**
 * Check if a string contains another string based on sensitivity settings
 *
 * @param haystack - The string to search in
 * @param needle - The string to search for
 * @param sensitivity - The sensitivity setting to use
 * @param collator - Optional precreated collator
 * @returns Whether the haystack contains the needle
 */
export function contains(
  haystack: string,
  needle: string,
  sensitivity: Sensitivity = 'base',
  collator: Intl.Collator = createCollator(sensitivity),
): boolean {
  if (!needle) {
    return true;
  }

  if (!haystack) {
    return false;
  }

  // For base sensitivity (case and accent insensitive), we can do a simpler check
  if (sensitivity === 'base') {
    return haystack.toLowerCase().includes(needle.toLowerCase());
  }

  // For other sensitivities, we need to check each substring
  const haystackLength = haystack.length;
  const needleLength = needle.length;

  if (needleLength > haystackLength) {
    return false;
  }

  for (let i = 0; i <= haystackLength - needleLength; i++) {
    const substring = haystack.substring(i, i + needleLength);
    if (collator.compare(substring, needle) === 0) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a string starts with another string based on sensitivity settings
 *
 * @param haystack - The string to check
 * @param needle - The prefix to check for
 * @param sensitivity - The sensitivity setting to use
 * @param collator - Optional precreated collator
 * @returns Whether the haystack starts with the needle
 */
export function startsWith(
  haystack: string,
  needle: string,
  sensitivity: Sensitivity = 'base',
  collator: Intl.Collator = createCollator(sensitivity),
): boolean {
  if (!needle) {
    return true;
  }

  if (!haystack) {
    return false;
  }

  // For base sensitivity (case and accent insensitive), we can do a simpler check
  if (sensitivity === 'base') {
    return haystack.toLowerCase().startsWith(needle.toLowerCase());
  }

  // For other sensitivities with proper collation
  const substring = haystack.substring(0, needle.length);
  return collator.compare(substring, needle) === 0;
}

/**
 * Filter an array of items based on a query string and a filtering function
 *
 * @param items - The items to filter
 * @param query - The query string to filter with
 * @param filterFn - The filtering function to use
 * @returns The filtered items
 */
export function filterItems<T extends { label: ReactNode }>(
  items: T[],
  query: string,
  filterFn: (item: string, query: string) => boolean,
): T[] {
  if (!query) {
    return items;
  }

  return items.filter((item) => {
    // Convert ReactNode label to string if needed
    const labelStr = typeof item.label === 'string' ? item.label : item.label?.toString() || '';
    return filterFn(labelStr, query);
  });
}

/**
 * Create a filter object with the specified options
 *
 * @param options - Configuration options for filtering
 * @returns A filter object with contains, startsWith, and filterItems methods
 */
export function createFilter(options: FilterOptions = {}) {
  const sensitivity = options.sensitivity || 'base';
  const collator = createCollator(sensitivity);

  return {
    contains: (haystack: string, needle: string) => contains(haystack, needle, sensitivity, collator),

    startsWith: (haystack: string, needle: string) => startsWith(haystack, needle, sensitivity, collator),

    filterItems: <T extends { label: ReactNode }>(
      items: T[],
      query: string,
      filterFn: (item: string, query: string) => boolean,
    ) => filterItems(items, query, filterFn),
  };
}
